import clsx from 'clsx';
import type { Medication } from '@/types/db';
import { isMedActive } from '@/features/timeline/lib/timelineQueries';

function formatShortDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

interface Props {
  medications: Medication[];
  onOpenEvent: (eventId: string) => void;
}

export function ProfileMedTab({ medications, onOpenEvent }: Props) {
  if (medications.length === 0) {
    return (
      <div className="card p-6 text-center">
        <p className="font-medium mb-1">No medications recorded</p>
        <p className="text-sm text-ink-muted">
          Add a prescription to see medication history here.
        </p>
      </div>
    );
  }

  const today = new Date().toISOString().slice(0, 10);
  const active = medications.filter((m) => !m.is_stopped && isMedActive(m, today, null));
  const expired = medications.filter((m) => !m.is_stopped && !isMedActive(m, today, null));
  const stopped = medications.filter((m) => m.is_stopped);
  const showHeaders = medications.length > 8;

  return (
    <div className="space-y-3">
      {showHeaders && active.length > 0 && (
        <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
          Active ({active.length})
        </p>
      )}
      {active.map((m) => (
        <MedCard key={m.id} med={m} status="active" onOpenEvent={onOpenEvent} />
      ))}

      {expired.length > 0 && (
        <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted pt-2">
          Expired ({expired.length})
        </p>
      )}
      {expired.map((m) => (
        <MedCard key={m.id} med={m} status="expired" onOpenEvent={onOpenEvent} />
      ))}

      {showHeaders && stopped.length > 0 && (
        <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted pt-2">
          Past ({stopped.length})
        </p>
      )}
      {stopped.map((m) => (
        <MedCard key={m.id} med={m} status="stopped" onOpenEvent={onOpenEvent} />
      ))}
    </div>
  );
}

type MedStatus = 'active' | 'expired' | 'stopped';

function MedCard({
  med,
  status,
  onOpenEvent,
}: {
  med: Medication;
  status: MedStatus;
  onOpenEvent: (id: string) => void;
}) {
  const tags: string[] = [
    med.dosage ?? null,
    med.frequency ?? null,
    med.duration_days != null ? `${med.duration_days}d` : null,
  ].filter(Boolean) as string[];

  return (
    <div
      className="card px-4 py-3.5 space-y-2"
    >
      {/* Drug name + date on one row */}
      <div className="flex items-start justify-between gap-2">
        <p className="font-bold text-base text-ink leading-snug">
          {med.drug_name ?? 'Unnamed drug'}
        </p>
        {med.start_date && (
          <span className="text-xs text-ink-muted tabular-nums shrink-0 mt-0.5">
            {formatShortDate(med.start_date)}
          </span>
        )}
      </div>

      {/* Dose pill tags */}
      {tags.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-canvas border border-border text-[11px] font-medium text-ink-muted"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Status badge + view prescription */}
      <div className="flex items-center justify-between gap-2 pt-0.5">
        <span
          className={clsx(
            'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold',
            status === 'active'
              ? 'bg-[#E6F4EA] text-[#2E7D32]'
              : status === 'expired'
                ? 'bg-blue-50 text-flag-info'
                : 'bg-canvas text-ink-subtle border border-border',
          )}
        >
          {status === 'active' ? 'Active' : status === 'expired' ? 'Expired' : 'Stopped'}
        </span>
        <button
          type="button"
          onClick={() => onOpenEvent(med.event_id)}
          className="inline-flex items-center gap-1 text-xs text-ink-subtle font-medium min-h-[44px] hover:text-ink-muted"
          aria-label={`View source prescription for ${med.drug_name ?? 'this medication'}`}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-3 w-3 shrink-0"
            aria-hidden="true"
          >
            <path d="M9 12h6M9 16h6M17 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z" />
          </svg>
          View prescription
        </button>
      </div>
    </div>
  );
}
